import { FC, useEffect, useState } from 'react';
import { Box, Typography, Stack, Switch, Tabs, Tab } from '@mui/material';
import { Colors, SECTION_PADDING } from '../../styles/theme';
import SectionTitle from '../section-title';
import PricingCard from './PricingCard';
import EducationForm from './EducationForm';

export interface PricingPlan {
  id: string;
  name: string;
  description?: string;
  monthlyPrice: number | null;
  yearlyPrice: number | null; // 연간 결제 시 월 가격 (yearlyTotal = yearlyPrice * 12)
  originalMonthly?: number;
  originalYearlyPrice?: number; // 연간 결제 시 원래 월 가격
  features: string[];
  highlighted?: boolean;
  buttonText?: string;
  customPriceLabel?: string;
}

const PRICING_PLANS: PricingPlan[] = [
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 99,
    yearlyPrice: 79,
    originalMonthly: 199,
    originalYearlyPrice: 199,
    features: [
      'AI diagnostics',
      'Image analysis',
      'Voice input',
      'Vehicle database',
    ],
    highlighted: true,
    buttonText: 'Get Started',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: null,
    yearlyPrice: null,
    customPriceLabel: 'Custom',
    features: ['Everything in Pro', 'Advanced AI model', 'More requests'],
    highlighted: false,
    buttonText: 'Contact',
  },
];

const PricingSection: FC = () => {
  const [isYearly, setIsYearly] = useState(false);
  const initialTab = window.location.hash === '#education' ? 1 : 0;
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    const onHash = () => {
      if (window.location.hash === '#education') {
        setTab(1);
        // Scroll to the pricing section since #education lives inside it
        requestAnimationFrame(() => {
          document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  return (
    <Box
      id="pricing"
      sx={{
        width: '100%',
        backgroundColor: '#FAFAFA',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: SECTION_PADDING.py,
        px: SECTION_PADDING.px,
      }}
    >
      <SectionTitle title="Simple" subtitle="Pricing" />

      {/* Plan / Education Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mt: { xs: 3, md: 4 },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: { xs: '14px', md: '16px' },
            color: '#9E9E9E',
            px: { xs: 2, md: 4 },
          },
          '& .Mui-selected': {
            color: `${Colors.blue} !important`,
          },
          '& .MuiTabs-indicator': {
            backgroundColor: Colors.blue,
          },
        }}
      >
        <Tab label="Plans" />
        <Tab label="Education" />
      </Tabs>

      {tab === 0 && (
        <>
          {/* Toggle Switch */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={{
              xs: 1,
              md: 2,
            }}
            sx={{
              my: {
                xs: 3,
                md: 4,
              },
            }}
          >
            <Typography
              sx={{
                fontWeight: isYearly ? 400 : 600,
                color: isYearly ? '#9E9E9E' : '#38383B',
                fontSize: '16px',
              }}
            >
              Monthly
            </Typography>
            <Switch
              checked={isYearly}
              onChange={() => setIsYearly(!isYearly)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: Colors.blue,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: Colors.blue,
                },
              }}
            />
            <Typography
              sx={{
                fontWeight: isYearly ? 600 : 400,
                color: isYearly ? '#38383B' : '#9E9E9E',
                fontSize: '16px',
              }}
            >
              Yearly
            </Typography>
          </Stack>

          {/* Pricing Cards */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            alignItems={{ xs: 'center', md: 'stretch' }}
            justifyContent="center"
            width="100%"
          >
            {PRICING_PLANS.map((plan) => (
              <PricingCard key={plan.id} plan={plan} isYearly={isYearly} />
            ))}
          </Stack>
        </>
      )}

      {tab === 1 && (
        <Box id="education" sx={{ mt: { xs: 3, md: 4 }, width: '100%' }}>
          <EducationForm />
        </Box>
      )}
    </Box>
  );
};

export default PricingSection;
